import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Building } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConciergeSettings } from "@/components/admin/ConciergeSettings";
import { AdminCommissions } from "@/components/admin/AdminCommissions";
import { ConciergeArticles } from "@/components/admin/ConciergeArticles";
import { DepositHistoryList } from "@/components/concierge/DepositHistoryList";
import { CollapsibleBlock } from "@/components/admin/CollapsibleBlock";
import { printConciergeTicket } from "@/components/admin/printConciergeTicket";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/concierge`;

export default function AdminConcierge() {
  const navigate = useNavigate();
  const auth = localStorage.getItem("adminAuth");
  const headers = { Authorization: `Basic ${auth}` };
  const [deposits, setDeposits] = useState([]);
  const [version, setVersion] = useState(0);

  const loadDeposits = useCallback(() => {
    axios.get(`${API}/deposits`, { headers: { Authorization: `Basic ${auth}` } })
      .then((r) => setDeposits(r.data))
      .catch((e) => { if (e.response?.status === 401) navigate("/admin"); });
  }, [auth, navigate]);

  useEffect(() => {
    if (!auth) { navigate("/admin"); return; }
    loadDeposits();
  }, [auth, navigate, loadDeposits]);

  const deleteDeposit = async (d) => {
    if (!window.confirm(`Supprimer définitivement le bon ${d.rawNumber} (${d.total.toFixed(2)} €) ?\nLes parts du mois seront recalculées.`)) return;
    try {
      const res = await axios.delete(`${API}/deposits/${d.id}`, { headers });
      toast.success(`Bon supprimé${res.data.credit_refunded ? ` — ${res.data.credit_refunded.toFixed(2)} € d'avoir rendu` : ""}`);
      if (res.data.was_paid) toast.warning("Ce mois était déjà marqué « Payé » : le montant a changé, pensez à régulariser.", { duration: 10000 });
      loadDeposits();
      setVersion((v) => v + 1);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  if (!auth) return null;

  const labelled = deposits.map((d) => ({ ...d, rawNumber: d.number, number: `${d.number} — ${d.person_name}${d.company_name ? ` (${d.company_name})` : " (Admin)"}` }));

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8" data-testid="admin-concierge-page">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate("/admin/dashboard")} data-testid="admin-concierge-back-btn"><ArrowLeft className="w-4 h-4 mr-1" />Retour</Button>
          <Building className="w-7 h-7 text-pink-600" />
          <h1 className="text-2xl md:text-3xl font-bold">Conciergerie</h1>
        </div>
        <AdminCommissions headers={headers} version={version} />
        <CollapsibleBlock title="Accès de la conciergerie" testid="concierge-settings-block">
          <ConciergeSettings headers={headers} />
        </CollapsibleBlock>
        <CollapsibleBlock title="Articles et prix fixes" testid="concierge-articles-block">
          <ConciergeArticles headers={headers} />
        </CollapsibleBlock>
        <div className="bg-white rounded-xl border p-4 space-y-3">
          <h2 className="font-bold">Bons de dépôt reçus ({deposits.length})</h2>
          <DepositHistoryList deposits={labelled} onDelete={deleteDeposit} onPrint={printConciergeTicket} />
        </div>
      </div>
    </div>
  );
}
